<template>
  <CModal :title="modalTitle"
          :size="changesAvailable ? 'lg' : ''"
          :color="modeColor"
          :show="show"
          :centered="true"
          @update:show="handleCancel">

    <div class="content">
      <div v-if="changesAvailable" class="modal-title">
        <h3>
          <template v-if="mode === 'edit'">Changed</template>
          <template v-if="mode === 'remove'">Removed</template>
          polygons:
        </h3>
      </div>

      <template v-if="errors.length">
        <CAlert v-for="(error, index) in errors" :key="`error-${index}`" color="danger" closeButton>
          {{ error }}
        </CAlert>
      </template>

      <CRow>
        <CCol col="12">

          <template v-if="changesAvailable">
            <CDataTable
              :class="''"
              :striped="true"
              :items="formattedPolygons"
              :fields="fields"
              :itemsPerPage="table.itemsPerPage"
              :pagination="formattedPolygons.length >= table.itemsPerPage"
              :sorter="true"
              :table-filter="false"
              :loading="false"
              :active-page="1"
              :hover="true"
              :responsive="true">
            </CDataTable>
          </template>

          <template v-else>
            <div class="empty-changes">Nothing changed</div>
          </template>
        </CCol>
      </CRow>
    </div>

    <template #footer>
      <template v-if="changesAvailable && !autoclose">
        <CButton @click="handleCancel" :disabled="processing" color="secondary">
          Cancel
        </CButton>
        <CButton @click="handleConfirm" :color="modeColor" :disabled="processing">
          <template v-if="mode === 'edit'">Save</template>
          <template v-if="mode === 'remove'">Remove</template>
        </CButton>
      </template>

      <template v-if="!changesAvailable">
        <CButton @click="handleCancel" color="secondary">
          Close
          <template v-if="autoclose">
            &nbsp;({{ autoclose }})
          </template>
        </CButton>
      </template>

      <template v-if="autoclose">
        <CButton @click="handleForceAutoClose" color="secondary">
          Close ({{ autoclose }})
        </CButton>
      </template>

    </template>

  </CModal>
</template>

<script>
import { mapActions } from 'vuex'
import getNested from 'lodash-es/get'
import startCase from 'lodash-es/startCase'

const autoCloseTimeout = 5;


export default {
  name: 'map-entities-save-state-modal',

  components: {},

  props: {
    show: {
      type: Boolean,
      default: false,
    },
    mode: {
      type: String,
      default: 'edit',  // "edit" or "remove"
    },
    changedLayers: {
      type: Array,
      default: null,
    },
  },

  data() {
    return {
      processing: false,
      autoclose: false,  // autoclose timeout in seconds
      autocloseTimer: null,

      errors: [],
      formattedPolygons: [],
      table: {
        itemsPerPage: 10,
      },
    };
  },

  methods: {
    ...mapActions(
      'polygons', [
        // save methods
        'updatePort',
        'updateBerth',
        'updateShipyard',
        'updateAnchorage',

        // delete methods
        'deleteBerth',
      ],
    ),

    handleCancel() {
      this.$emit('update:show', false);
      this.$emit('cancel');
    },

    handleForceAutoClose() {
      // when "Close (5..0)" button is clicked
      clearInterval(this.autocloseTimer);
      this.autoclose = false;
      this.$emit('update:show', false);
      this.$emit('save', this.formattedPolygons);
    },

    async handleConfirm() {
      await this.saveChangedEntities();
    },

    async saveChangedEntities() {
      this.processing = true;
      for(let item of this.formattedPolygons) {
        let result = {};
        let apiData = this.getEntityAPIData(item);
        let apiMethod = this.getAPIMethod(item.type);

        if(apiMethod) {
          result = await apiMethod(apiData);
          item.saved = result.error ? 'ERROR' : 'Ok';
          item.removed = item.saved;  // same behaviour for both columns
        } else {
          item.saved = 'NOT ALLOWED';
          item.removed = item.saved;  // same behaviour for both columns
        }

      }
      this.runAutoclose();
      this.processing = false;
    },

    getAPIMethod(itemType) {
      let methodType = 'update';
      if(this.mode === 'remove') {
        methodType = 'delete';
      }

      const methodName = `${methodType}${startCase(itemType)}`;
      if(this[methodName]) {
        return this[methodName];
      } else {
        console.error(`Method ${methodName} is not allowed!`);
        return null;
      }
    },

    getEntityAPIData(entity) {
      return {
        portId: entity.portId,
        primaryPort: entity.portId,  // for anchorages
        geometryPolygon: JSON.stringify(
          getNested(entity, 'geoJSON.geometry', {})
        ),
        [`${entity.type}Id`]: entity.id,
        [`${entity.type}Name`]: entity.name,  // required for shipyards
      };
    },

    runAutoclose() {
      this.autoclose = autoCloseTimeout;

      this.autocloseTimer = setInterval(
        () => {
          this.autoclose -= 1;
          if(this.autoclose === 0 ) {
            this.handleForceAutoClose();
          }
        },
        1000
      );

    },
  },

  computed: {
    changesAvailable() {
      return !!this.formattedPolygons.length;
    },

    modalTitle() {
      return `Do you want to ${this.mode} the edited polygons?`;
    },

    modeColor() {
      return this.mode === 'edit' ? 'success' : 'danger';
    },

    fields() {
      return [
        { key: 'id', label: 'Id' },
        { key: 'type', label: 'Type' },
        { key: 'name', label: 'Name' },
        { key: 'portId', label: 'Port Id' },
        this.actionField,
      ]
    },

    actionField() {
      if (this.mode === 'remove') {
        return { key: 'removed', label: 'Removed', _classes: 'text-center'};
      }
      // edit mode by default
      return { key: 'saved', label: 'Saved', _classes: 'text-center'};
    },
  },

  watch: {
    changedLayers() {
      this.formattedPolygons = this.changedLayers.map(layer => {
        return {
          ...layer.options.zone,
          portId: layer.options.zone.portId || layer.options.zone.primaryPort,
          geoJSON: layer.toGeoJSON(),  // updated geoJSON
          saved: '...',
          removed: '...',
        };
      });
    },
  },
}
</script>

<style scoped>

</style>

<style scoped lang="scss">
  .modal-title {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
  }

  .empty-changes {
    margin: 20px 0;
    text-align: center;
    font-size: 18px;
    color: #787878;
  }

</style>
